/**
 * Copyright (C) 2019 HERE Global B.V. and its affiliate(s).
 * All rights reserved.
 *
 * This software and other materials contain proprietary information
 * controlled by HERE and are protected by applicable copyright legislation.
 * Any use and utilization of this software and other materials and
 * disclosure to any third parties is conditional upon having a separate
 * agreement with HERE for the access, use, utilization or disclosure of this
 * software. In the absence of such agreement, the use of the software is not
 * allowed.
 */

import LuiInputBase from '@here/lui-base/lui-input-base'
import { LuiSuggestionsMenuMixin } from '@here/lui-base/lui-suggestions-menu-mixin'
import scopedCss from 'lui-tagfield.scss'
import template from 'lui-tagfield.html'

const KEYCODE_BACKSPACE = 8
const KEYCODE_TAB = 9
const KEYCODE_ENTER = 13

export default class LuiTagfield extends LuiSuggestionsMenuMixin(LuiInputBase) {
  constructor() {
    super({ delegatesFocus: true })
  }

  static get is() {
    return 'lui-tagfield'
  }

  static get attributeToggles() {
    return [
      'disabled',
      'placeholder',
    ]
  }

  static get css() {
    return scopedCss
  }

  static get template() {
    return template
  }

  static get hasLuiElementsInShadowDom() {
    return false
  }

  get uiWrapper() {
    this._uiWrapper = this._uiWrapper || this.shadowRoot.querySelector('.lui-tagfield__wrapper')
    return this._uiWrapper
  }

  get tagsWrapper() {
    return this.uiWrapper.querySelector('.lui-tagfield__tags')
  }

  get tags() {
    return this.tagsWrapper.querySelectorAll('lui-tag')
  }

  get select() {
    return this.querySelector('select')
  }

  get selectedOptions() {
    return this.querySelectorAll('option:checked')
  }

  get disabled() {
    return this.hasAttribute('disabled')
  }

  get textfield() {
    return this.uiWrapper.querySelector('lui-textfield')
  }

  get field() {
    return this.textfield.field
  }

  disabledChanged() {
    super.disabledChanged()
    this.toggleCssClass('lui-disabled', this.disabled)

    const _disabledChanged = () => {
      if (this.disabled) {
        this._unregisterEvents()
        this.field.setAttribute('disabled', 'disabled')
        this.textfield.setAttribute('disabled', 'disabled')
      } else {
        this._registerEvents()
        this.field.removeAttribute('disabled')
        this.textfield.removeAttribute('disabled')
      }
    }

    const _isSeedElementAvailable = () => {
      return !!this.seedElement
    }

    this.poll(_disabledChanged, _isSeedElementAvailable)
  }

  init() {
    super.init()

    this.seedElement = this.select
    this.selectedOptions.forEach(option => {
      this._createTag(option.value)
    })

    this.field.tabIndex = -1
    this._setPlaceholder()
  }

  onConnected() {
    super.onConnected()
    this.delay(() => {
      this._registerEvents()
      if (this.useSuggestions) {
        this._registerSuggestionsEvents()
      }
    })
  }

  onDisconnected() {
    super.onDisconnected()

    this._unregisterEvents()
    this._unregisterSuggestionsEvents()
  }

  placeholderChanged(oldVal, newVal) {
    this._setPlaceholder(newVal)
  }

  _addOption(name) {
    if (!name || this._tagAlreadyExists(name)) {
      return
    }

    const option = this._getOption(name)
    if (option) {
      option.setAttribute('selected', 'selected')
    } else {
      const option = document.createElement('option')
      option.textContent = name
      option.setAttribute('selected', 'selected')
      this.select.appendChild(option)
    }

    this._dispatchChangeEvent()
    this.dispatchEvent(new CustomEvent('lui-tag:added', { bubbles: true, composed: true }))
  }

  _createTag(text) {
    const tagElement = document.createElement('lui-tag')
    tagElement.textContent = text
    tagElement.setAttribute('removable', 'removable')
    tagElement.classList.add('lui-medium')
    if (this.disabled) {
      tagElement.setAttribute('disabled', 'disabled')
    }
    this.tagsWrapper.insertBefore(tagElement, this.textfield)
    this._suggestionsUpdate()
  }

  _getOption(name) {
    return Array.from(this.select.options).find(option => option.textContent === name)
  }

  _handleAttributeMutation(mutation) {
    const textContent = mutation.target.textContent
    if (mutation.target.selected) {
      this._createTag(textContent)
    } else {
      this._removeTagByName(textContent)
    }
  }

  _handleChildListMutation(mutation) {
    if (mutation.removedNodes) {
      mutation.removedNodes.forEach(node => {
        this._removeTagByName(node.textContent)
      })
    }
    if (mutation.addedNodes) {
      mutation.addedNodes.forEach(node => {
        if (node.attributes && node.attributes.selected) {
          this._createTag(node.textContent)
        }
      })
    }
  }

  _handleKeyPressedBackspace() {
    if (this.field.value) {
      return
    }

    const tag = this.tagsWrapper.querySelector('lui-tag:last-of-type')
    if (!tag) {
      return
    }
    if (tag.hasAttribute('selected')) {
      this._removeOptionByName(tag.textContent)
    } else {
      tag.setAttribute('selected', 'selected')
    }
  }

  _handleKeyPressedDefault() {
    this.tags.forEach(tag => tag.removeAttribute('selected'))
  }

  _addNewTag() {
    this._addOption(this.textfield.value)
    this.textfield.value = ''
    this._suggestionsUpdate()
  }

  _handleKeyPressedTab(event) {
    if (this.textfield.value) {
      this.field.focus()
      event.stopPropagation()
      event.preventDefault()
    }
    this._addNewTag()
    this._suggestionsUpdate()
  }

  _handleKeyPressedEnter() {
    this._addNewTag()
    this._suggestionsUpdate()
  }

  _onBlur(event) {
    super._onBlur()

    event.stopPropagation()
    this.classList.remove('lui-focus')
  }

  _onClick(event) {
    event.stopPropagation()
    this.classList.add('lui-focus')
    this.field.focus()
  }

  _onKeyPressed(event) {
    switch (event.keyCode) {
      case KEYCODE_BACKSPACE:
        this._handleKeyPressedBackspace()
        break
      case KEYCODE_TAB:
        this._handleKeyPressedTab(event)
        break
      case KEYCODE_ENTER:
        this._handleKeyPressedEnter()
        break
      default:
        this._handleKeyPressedDefault()
    }
  }

  _onMutationOptions(mutations) {
    mutations.forEach((mutation) => {
      if (mutation.type === 'attributes' && mutation.attributeName === 'selected') {
        this._handleAttributeMutation(mutation)
      } else if (mutation.type === 'childList') {
        this._handleChildListMutation(mutation)
      }
    })
  }

  _onRemoveClicked(event) {
    this._removeOptionByName(event.target.textContent)
    this._suggestionsUpdate()
  }

  _registerEvents() {
    if (this.disabled) {
      return
    }
    this._onBlurBound = this._onBlurBound || this._onBlur.bind(this)
    this._onClickBound = this._onClickBound || this._onClick.bind(this)
    this._onRemoveClickedBound = this._onRemoveClickedBound || this._onRemoveClicked.bind(this)
    this._onKeyPressedBound = this._onKeyPressedBound || this._onKeyPressed.bind(this)
    this.addEventListener('blur', this._onBlurBound)
    this.addEventListener('click', this._onClickBound)
    this.uiWrapper.addEventListener('lui-tag:removeClicked', this._onRemoveClickedBound)
    this.field.addEventListener('keydown', this._onKeyPressedBound)

    if (this.select) {
      this._optionsObserver = window.lui.createMutationObserver(this._onMutationOptions.bind(this))
      this._optionsObserver.observe(this.select,
        {
          childList: true,
          subtree: true,
          attributes: true,

          // characterData: true,
          //  FIXME Angular falls into infinite loop if characterData is set to true
          //  The caus eis that Angular replaces original MutationObserver with its wrapper.
          //  The original one is accessible by: window[window.Zone.__symbol__("MutationObserver")]
          //  https://github.com/angular/angular/issues/26948
        })
    }
  }

  _removeOptionByName(tagname) {
    this.selectedOptions.forEach(option => {
      if (option.textContent === tagname) {
        option.removeAttribute('selected')
      }
    })
    this._dispatchChangeEvent()
    this._suggestionsUpdate()
  }

  _removeTagByName(name) {
    this.tags.forEach(tag => {
      if (tag.textContent === name) {
        tag.remove()
      }
    })
    this._suggestionsUpdate()
  }

  _setPlaceholder(newVal) {
    const placeholder = (newVal || newVal === '') ? newVal : null
    if (placeholder !== null) {
      this.textfield.setAttribute('placeholder', placeholder)
    }
  }

  _tagAlreadyExists(name) {
    const option = this._getOption(name)
    return option && option.hasAttribute('selected')
  }

  _unregisterEvents() {
    this.removeEventListener('blur', this._onBlurBound)
    this.removeEventListener('click', this._onClickBound)
    this.uiWrapper.removeEventListener('lui-tag:removeClicked', this._onRemoveClickedBound)
    this.field.removeEventListener('keydown', this._onKeyPressedBound)
    if (this._optionsObserver) {
      this._optionsObserver.disconnect()
    }
  }

  get uiMenu() {
    if (!this.field) {
      return
    }
    this._uiMenu = this._uiMenu || this.createMenu()
    return this._uiMenu
  }

  _update() {
    this._refreshOptions()
    this._updateUiMenu(this._getFilteredOptions(this.searchText))
  }

  _onFocus() {
    super._onFocus()

    this.classList.add('lui-focus')
    this.field.focus()
  }

  _updateTargetInput(value) {
    if (value !== null) {
      this.suggestionsTargetInput.value = this._normalizeTextContent(value)
      this._addNewTag()
    }
  }

  _suggestionsOptionsFilter(item) {
    return !this._tagAlreadyExists(item.shortLabel)
  }

  _searchTextFilter(searchText, item) {
    return item.textContent.toLowerCase().includes(searchText) && !this._tagAlreadyExists(item.textContent)
  }

  get tagValues() {
    return Array.from(this.selectedOptions).map(option => option.value)
  }

  _dispatchChangeEvent() {
    this.dispatchEvent(new CustomEvent('lui-tagfield:change', {
      bubbles: false,
      composed: false,
      detail: this.tagValues }))
  }
}



//////////////////
// WEBPACK FOOTER
// ./packages/lui-tagfield/lui-tagfield.js
// module id = 352
// module chunks = 0 1 4